// One place that turns an API failure into a sentence, so five screens cannot each
// invent their own.
//
// The body of this file is P01's implementation (their post 11:37 §4), taken almost
// verbatim: the logic was already written and falsified, and rewriting it to make it
// mine would have cost the swarm an hour for nothing. What changed is the dictionary
// half, which is the half MGR left to this file's owner — and it changed in ways that
// matter more than the code did. See the CTA table.
//
// THE SHAPE
//   describeApiError(err, t) -> { title, body, retry, cta? }
//
// A denial (403 with a code) is not a failure: retrying it changes nothing, so `retry`
// is false and the sentence IS the message. Everything else is the generic error state,
// where retry is the only honest offer.

import { ApiError } from '../../lib/api'
import type { TFunc } from '../../lib/i18n'

export interface ApiErrorCta {
  label: string
  to: string
  external?: boolean
}

export interface DescribedApiError {
  title: string
  /** Null for a denial: the sentence is the whole message and a second line would pad it. */
  body: string | null
  retry: boolean
  cta?: ApiErrorCta
}

/**
 * Where a denial can send someone — and it is much shorter than the list of denials.
 *
 * P01 proposed four targets. Three of them do not survive being checked against where
 * the code is actually raised, so they are not here, and each absence is deliberate:
 *
 *   email_unverified   raised at LOGIN (auth/routes.py:233), so the person has no
 *                      session and `/settings` is unreachable for them. The resend
 *                      endpoint IS public (auth/routes.py:487, and its docstring says
 *                      why), but no PUBLIC SCREEN offers it — so there is nowhere to
 *                      send them today. Reported rather than papered over with a link
 *                      that lands on a login wall.
 *   autopilot_disabled `/settings` was a guess: nothing measured says the toggle lives
 *                      there, and a button that does not fix the thing it is attached
 *                      to is worse than no button.
 *   country_not_supported  `/pricing` does not make a country supported. The sentence
 *                      already says the true thing; a CTA would imply a way around it.
 *
 * What is left is the one that is unambiguous: a suspended account is a conversation,
 * and the address is the one the product already uses in 52 places.
 */
const CTA: Record<string, { to: string; external?: boolean }> = {
  account_suspended: { to: 'mailto:kontakt@edimi.ai', external: true },
}

/**
 * `t()` returns the KEY when nobody has it (lib/i18n.tsx:52), so `t(k) === k` is the
 * only test of presence there is — and printing a key at a customer is exactly what
 * the header of the `errors` namespace warns against. Every read here goes through this.
 */
function tOr(t: TFunc, key: string, fallback: string | null): string | null {
  const value = t(key)
  return value === key ? fallback : value
}

/**
 * The server's error code, but only from a shape we can trust: a 403 or 409 with
 * `{error: "..."}`.
 *
 * 409 WAS ADDED 2026-08-30, and it is a decision about MEANING rather than about a number.
 * P02 measured the consequence of leaving it out: `gems_on_hold` is a 409, so the campaign
 * bar fell through to "something went wrong on our side, try again" — untrue twice over,
 * because nothing went wrong and retrying changes nothing.
 *
 * All ten 409 codes this backend returns describe a STATE, not a failure:
 * already_published, campaign_cancelled, campaign_not_editable, campaign_not_pausable,
 * campaign_not_paused, gems_on_hold, no_audio, nothing_delivered, pack_unavailable,
 * render_in_flight. Retrying any of them clicks into a wall. `render_in_flight` is the one
 * where a retry works EVENTUALLY, and its sentence says wait rather than promising a retry.
 *
 * Not extended further than 409 on purpose: a 500 or a 502 IS a failure and retry is the
 * honest offer there, which is what the generic branch below exists for.
 *
 * EXPORTED because a screen sometimes has to do more than print the sentence. Login is
 * the case: an `email_unverified` denial gets an inline "send the link again" action that
 * posts back to a public endpoint, and no CTA table can express that — a CTA is a link to
 * somewhere, this is a request from here. So the caller reads the code and decides.
 */
export function denialCode(err: unknown): string | null {
  if (!(err instanceof ApiError) || (err.status !== 403 && err.status !== 409)) return null
  const body = err.body as { error?: unknown } | null
  return typeof body?.error === 'string' && body.error ? body.error : null
}

export function describeApiError(err: unknown, t: TFunc): DescribedApiError {
  const code = denialCode(err)

  // A 403 or 409 with no readable body (api.ts leaves `body` null when the response is
  // not JSON) is NOT treated as a denial, and that is a decision rather than a gap: the
  // commonest cause of a bodyless 403 here is a stale CSRF token, which a retry really
  // does fix. Claiming "you are not allowed" when we do not know would be worse than
  // offering the retry that usually works.
  if (!code) {
    return { title: t('common.error.title'), body: t('common.error.body'), retry: true }
  }

  // An unknown code must never reach the screen as a code. `errors.unknown` is the
  // documented last resort of that namespace and it says the true thing: something on
  // our side, try again.
  const sentence = tOr(t, `errors.${code}`, null) ?? t('errors.unknown')
  const target = CTA[code]
  const label = tOr(t, `errors.cta.${code}`, null)

  return {
    title: sentence,
    body: null,
    retry: false,
    // Both halves required: a target with no label would render a nameless button, and
    // a label with no target would render a link to nowhere. Neither is better than the
    // sentence on its own, which is the fallback.
    ...(target && label ? { cta: { label, to: target.to, external: target.external } } : {}),
  }
}
